/**
 * PublicRubricPage
 *
 * Anonymous lead-magnet rubric tool at /tools/rubric. No auth required,
 * no save, no history. Visitor generates a rubric, sees the value,
 * gets prompted to sign up free to save and unlock more tools.
 *
 * Funnel:
 *   /tools/rubric (anon)  -> generate, see output
 *                         -> 'Save your rubrics. Sign up free →'
 *   /login                -> 3 free credits, real save, history
 *   credits-exhausted     -> paywall -> Pro
 *
 * Backend rate-limits to 5 generations per IP per 24h - if abuse becomes
 * a problem, Cloudflare at the edge will take over.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Download as DownloadIcon,
  FileText as FileTextIcon,
} from "lucide-react";
import Logo from "../../components/ui/Logo";

// ─────────────────────────────────────────────────────────────────
// SEO + SOCIAL SHARE METADATA
// ─────────────────────────────────────────────────────────────────
// Per-page overrides for the rubric lead-magnet. The shared
// frontend/public/index.html sets the SITE-WIDE defaults (homepage
// copy); on mount we MUTATE those static <meta>/<link>/<title>
// elements to rubric-specific content, and restore them on unmount.
//
// Why mutate instead of using React 19's <title>/<meta> in JSX:
// React 19 hoists JSX meta into <head>, but it doesn't dedupe
// against meta tags that already exist in the static index.html.
// The page ends up with two <meta name="description">, etc. Most
// crawlers and social-share scrapers pick the FIRST tag they see —
// the static one from index.html — so the rubric-specific meta
// would never reach them. Direct mutation guarantees the live tag
// is the one a crawler reads. (When the homepage's meta moves out
// of index.html into JSX, this can be reverted to the React 19
// pattern. iter #1, 2026-05-07.)

interface MetaSpec {
  /** <title> element textContent */
  title: string;
  /** <meta name="description"> content */
  description: string;
  /** og:title / og:description / og:url — Slack, LinkedIn, FB cards */
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  /** twitter:title / twitter:description */
  twitterTitle: string;
  twitterDescription: string;
  /** <link rel="canonical"> href — search-engine de-dup */
  canonical: string;
}

const RUBRIC_META: MetaSpec = {
  title: "Free AI Rubric Generator: Custom Rubrics in Under a Minute | TeacherDashboard",
  description:
    "Generate a custom assessment rubric in under a minute. Tell us the assignment, AI writes the descriptors, you edit and copy into your gradebook. Free, no signup. Curriculum-aligned for Ontario, BC, and other provinces.",
  ogTitle: "Free AI Rubric Generator: Custom Rubrics in Under a Minute",
  ogDescription:
    "Tell us the assignment, we'll write the rubric. Edit it however you want, copy it into your gradebook. Free, no signup, 5 generations per day.",
  ogUrl: "https://teacherdashboard.ai/tools/rubric",
  twitterTitle: "Free AI Rubric Generator: TeacherDashboard",
  twitterDescription:
    "Custom rubrics in under a minute. Free, no signup. Curriculum-aligned for Ontario, BC, and beyond.",
  canonical: "https://teacherdashboard.ai/tools/rubric",
};

/**
 * Apply per-page SEO + social meta. Returns a teardown function
 * that restores the original values, so navigating away from
 * /tools/rubric reverts to whatever index.html had.
 */
function applyMeta(spec: MetaSpec): () => void {
  const setMetaContent = (selector: string, value: string): (() => void) => {
    const el = document.querySelector<HTMLMetaElement>(selector);
    if (!el) return () => {};
    const original = el.getAttribute("content");
    el.setAttribute("content", value);
    return () => {
      if (original !== null) el.setAttribute("content", original);
    };
  };

  const restorers: Array<() => void> = [];

  // <title>
  const originalTitle = document.title;
  document.title = spec.title;
  restorers.push(() => {
    document.title = originalTitle;
  });

  restorers.push(setMetaContent('meta[name="description"]', spec.description));
  restorers.push(setMetaContent('meta[property="og:title"]', spec.ogTitle));
  restorers.push(
    setMetaContent('meta[property="og:description"]', spec.ogDescription),
  );
  restorers.push(setMetaContent('meta[property="og:url"]', spec.ogUrl));
  restorers.push(setMetaContent('meta[name="twitter:title"]', spec.twitterTitle));
  restorers.push(
    setMetaContent('meta[name="twitter:description"]', spec.twitterDescription),
  );

  // <link rel="canonical"> — index.html doesn't have one by default,
  // so we INJECT the link element rather than mutate.
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  let canonicalCreated = false;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
    canonicalCreated = true;
  }
  const originalCanonical = canonical.getAttribute("href");
  canonical.setAttribute("href", spec.canonical);
  restorers.push(() => {
    if (canonicalCreated) {
      canonical!.remove();
    } else if (originalCanonical !== null) {
      canonical!.setAttribute("href", originalCanonical);
    }
  });

  return () => {
    for (const restore of restorers.reverse()) restore();
  };
}

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

// Match the backend's CriterionI shape exactly (see
// backend/src/models/rubric.model.ts). The previous shape — with
// `name` instead of `title` and a `Level[]` of objects instead of
// `string[]` — predated the rubric model getting flattened. The
// runtime payload is `{ title, levels: string[] }`, so the old
// types caused every cell to render as undefined (the criteria
// table appeared empty after generation). 2026-05-07.
interface Criterion {
  title: string;
  levels: string[];
}

interface PublicRubric {
  title: string;
  description: string;
  header: string[];
  criteria: Criterion[];
}

interface GenerateResponse {
  rubric: PublicRubric;
  remaining: number;
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function PublicRubricPage() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [numCriteria, setNumCriteria] = useState(4);

  const [rubric, setRubric] = useState<PublicRubric | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Export state. pageSize is sticky so a teacher who picked A4 once
  // (international) doesn't have to re-pick on the next rubric.
  // Mirrors the worksheet export's persistence pattern.
  type PageSize = "letter" | "a4" | "tabloid" | "widescreen";
  const PAGE_SIZE_LABELS: Record<PageSize, string> = {
    letter: "Letter (8.5×11)",
    a4: "A4 (210×297mm)",
    tabloid: "Tabloid (11×17)",
    widescreen: "Widescreen 16:9",
  };
  const [pageSize, setPageSize] = useState<PageSize>(() => {
    try {
      const saved = localStorage.getItem("rubric-export-page-size");
      if (saved === "letter" || saved === "a4" || saved === "tabloid" || saved === "widescreen") {
        return saved;
      }
    } catch {
      // ignore — quota / private mode
    }
    return "letter";
  });
  useEffect(() => {
    try {
      localStorage.setItem("rubric-export-page-size", pageSize);
    } catch {
      // ignore
    }
  }, [pageSize]);
  const [isExporting, setIsExporting] = useState<null | "pdf" | "docx">(null);

  // Apply per-page SEO/social meta on mount, restore on unmount.
  // See applyMeta + RUBRIC_META above for why we mutate rather than
  // use React 19's <title>/<meta> in JSX.
  useEffect(() => applyMeta(RUBRIC_META), []);

  const canSubmit = title.trim().length > 0 && !isGenerating;

  // Double-click guard. canSubmit is computed from render-time
  // state (isGenerating). Between an onClick firing and React
  // committing setIsGenerating(true), both clicks of a rapid
  // double-tap pass the canSubmit check → two parallel fetches,
  // two AI generations charged. Same shape of bug fixed in the
  // dashboard wizard (commit 67240d8).
  const generatingRef = useRef(false);

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (generatingRef.current) return;
    generatingRef.current = true;

    setIsGenerating(true);
    setError(null);

    try {
      const res = await fetch("/api/public/rubric", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          grade: grade.trim() || undefined,
          subjects: subject.trim() || undefined,
          numCriteria,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Failed to generate rubric.");
      }

      const payload = data as GenerateResponse;
      setRubric(payload.rubric);
      setRemaining(typeof payload.remaining === "number" ? payload.remaining : null);
      window.scrollTo({ top: document.getElementById("output")?.offsetTop ?? 0, behavior: "smooth" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate rubric.");
    } finally {
      setIsGenerating(false);
      generatingRef.current = false;
    }
  }

  // Export the current rubric as PDF or DOCX. POSTs the rubric body
  // inline (anonymous users have nothing saved server-side) plus the
  // pageSize. Server returns the file; we trigger a browser download.
  // Same double-click guard pattern. Buttons disable when
  // isExporting !== null but a rapid double-click can fire two
  // onClicks before React commits the disabled state.
  const exportingRef = useRef(false);

  async function handleExport(format: "pdf" | "docx") {
    if (!rubric) return;
    if (exportingRef.current) return;
    exportingRef.current = true;
    setIsExporting(format);
    setError(null);
    try {
      const res = await fetch(`/api/public/rubric/export/${format}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: rubric.title,
          description: rubric.description,
          // Public rubric doesn't carry grade/subjects in state today,
          // but the BE accepts them — leave as undefined.
          header: rubric.header,
          criteria: rubric.criteria,
          pageSize,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Export failed (${res.status})`);
      }
      // Trigger download
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rubric.title.replace(/[^a-z0-9]+/gi, "-")}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to export as ${format.toUpperCase()}.`);
    } finally {
      setIsExporting(null);
      exportingRef.current = false;
    }
  }

  async function handleCopy() {
    if (!rubric) return;
    const lines: string[] = [rubric.title];
    if (rubric.description) lines.push(rubric.description);
    lines.push("");
    lines.push(["Criterion", ...rubric.header].join("\t"));
    for (const c of rubric.criteria) {
      lines.push([c.title, ...c.levels].join("\t"));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy. Browser blocked clipboard access.");
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-stone-50 font-sans text-stone-900 selection:bg-brand-100 selection:text-brand-900">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <Nav />

      {/* <main> landmark for SR navigation. Wraps the hero, form, and
          rubric output — everything between the global nav and footer.
          Without this, AT software has no shortcut to skip nav and
          jump to page content. */}
      <main id="main" tabIndex={-1}>
      <Hero />

      {/* Form card */}
      <section className="px-4 sm:px-6 lg:px-10 py-8">
        <div className="max-w-3xl mx-auto">
          <form
            onSubmit={handleGenerate}
            className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 md:p-8 space-y-5"
          >
            <div>
              <label htmlFor="rubric-title" className="block text-sm font-semibold text-stone-700 mb-1.5">
                Assignment title <span className="text-red-500">*</span>
              </label>
              <input
                id="rubric-title"
                name="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Persuasive essay on climate action"
                maxLength={200}
                required
                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 transition-colors"
              />

              {/* Quick-fill example chips — help first-time visitors
                  see what kind of input the tool expects + prime the
                  form so they can hit Generate in one click. Clicking
                  a chip overwrites whatever's in the form. Hidden
                  once a teacher has typed something so they don't
                  feel pressured to use a preset. */}
              {!title && !description && (
                <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                  <span className="text-xs text-stone-500 mr-1">Try with:</span>
                  {[
                    { t: "Persuasive Letter to Local MLA", g: "6", s: "English Language Arts", d: "Students write a 2-page persuasive letter on a community issue, with evidence and counter-arguments." },
                    { t: "Lab Report: Plant Growth Experiment", g: "5", s: "Science", d: "Hypothesis, methodology, data collection, analysis, and conclusion sections." },
                    { t: "Group Project: Inquiry on Local History", g: "8", s: "Social Studies", d: "Collaborative research project; students investigate a local historical event." },
                    { t: "Oral Presentation: Book Talk", g: "3", s: "English Language Arts", d: "3-5 minute oral presentation about a book the student has read." },
                  ].map((ex) => (
                    <button
                      key={ex.t}
                      type="button"
                      onClick={() => {
                        setTitle(ex.t);
                        setGrade(ex.g);
                        setSubject(ex.s);
                        setDescription(ex.d);
                      }}
                      className="px-2.5 py-1 text-xs bg-stone-50 hover:bg-brand-50 text-stone-700 hover:text-brand-800 border border-stone-200 hover:border-brand-300 rounded-full transition-colors"
                      title={`Pre-fill the form with ${ex.t}`}
                    >
                      {ex.t.split(":")[0].split(" to ")[0].slice(0, 28)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="rubric-grade" className="block text-sm font-semibold text-stone-700 mb-1.5">
                  Grade <span className="text-stone-500 font-normal">(optional)</span>
                </label>
                <input
                  id="rubric-grade"
                  name="grade"
                  type="text"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="e.g. Grade 5"
                  maxLength={50}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="rubric-subject" className="block text-sm font-semibold text-stone-700 mb-1.5">
                  Subject <span className="text-stone-500 font-normal">(optional)</span>
                </label>
                <input
                  id="rubric-subject"
                  name="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Language Arts"
                  maxLength={100}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="rubric-description" className="block text-sm font-semibold text-stone-700 mb-1.5">
                What you're assessing <span className="text-stone-500 font-normal">(optional)</span>
              </label>
              <textarea
                id="rubric-description"
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="A few sentences about what students should demonstrate. The AI uses this to make the rubric specific."
                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 transition-colors resize-none"
              />
            </div>

            <div>
              {/* The criteria-count is a button group, not a form input —
                  use role="group" + aria-label so AT software hears
                  "Number of criteria" before the digit buttons (instead
                  of just hearing "3, button" with no context). The
                  visible heading is a <span> not a <label> because no
                  single button is the labeled control. */}
              <span className="block text-sm font-semibold text-stone-700 mb-2" id="rubric-num-criteria-heading">
                Number of criteria
              </span>
              <div
                role="group"
                aria-labelledby="rubric-num-criteria-heading"
                className="flex flex-wrap gap-2"
              >
                {[3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNumCriteria(n)}
                    aria-pressed={numCriteria === n}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      numCriteria === n
                        ? "bg-brand-100 text-brand-800 border-brand-300"
                        : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              aria-busy={isGenerating}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-700 hover:bg-brand-800 disabled:bg-brand-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl text-base transition-colors shadow-sm"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  Generating your rubric…
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" aria-hidden="true" />
                  Generate rubric
                </>
              )}
            </button>

            <p className="text-center text-xs text-stone-500">
              No signup required. 5 free generations per day.
              {remaining !== null && (
                <> You have <span className="font-semibold text-stone-700">{remaining}</span> left today.</>
              )}
            </p>
          </form>
        </div>
      </section>

      {/* Output */}
      {rubric && (
        <section id="output" className="px-4 sm:px-6 lg:px-10 pb-12">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="p-6 md:p-8 border-b border-stone-100 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-stone-900">
                    {rubric.title}
                  </h2>
                  {rubric.description && (
                    <p className="mt-2 text-stone-600 max-w-2xl">{rubric.description}</p>
                  )}
                </div>
                {/* Export action group. PDF and DOCX both render
                    landscape (rubrics are wide tables) — the page-size
                    picker lets teachers pick Letter / A4 / Tabloid /
                    Widescreen depending on whether they're printing,
                    sharing internationally, posting on a wall, or
                    projecting. Sticky across runs (rubric-export-page-size
                    in localStorage) so a returning teacher's choice
                    persists. */}
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value as PageSize)}
                    aria-label="Export page size"
                    title="Page size for PDF / DOCX exports"
                    className="text-sm px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    {(Object.keys(PAGE_SIZE_LABELS) as PageSize[]).map((k) => (
                      <option key={k} value={k}>{PAGE_SIZE_LABELS[k]}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleExport("pdf")}
                    disabled={isExporting !== null}
                    aria-busy={isExporting === "pdf"}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isExporting === "pdf" ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <DownloadIcon className="w-4 h-4" aria-hidden="true" />
                    )}
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport("docx")}
                    disabled={isExporting !== null}
                    aria-busy={isExporting === "docx"}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isExporting === "docx" ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <FileTextIcon className="w-4 h-4" aria-hidden="true" />
                    )}
                    Word
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4 text-emerald-600" aria-hidden="true" />Copied</>
                    ) : (
                      <><Copy className="w-4 h-4" aria-hidden="true" />Copy</>
                    )}
                  </button>
                </div>
              </div>

              {/* Horizontal scroll for narrow viewports — at 390px
                  iPhone width, only ~2 of 4 level columns fit. We keep
                  the criterion column position:sticky so when a teacher
                  scrolls right to see Exemplary, they can still see
                  WHICH criterion they're reading. The shadow on the
                  right edge of the sticky column is a subtle hint that
                  more content is to the right. */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="sticky left-0 z-10 bg-stone-50 px-4 py-3 text-left font-semibold text-stone-700 border-b border-stone-200 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                        Criterion
                      </th>
                      {rubric.header.map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left font-semibold text-stone-700 border-b border-stone-200 min-w-[180px]"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rubric.criteria.map((c, ci) => {
                      const rowBg = ci % 2 === 0 ? "bg-white" : "bg-stone-50";
                      return (
                      <tr key={ci} className={rowBg}>
                        <td className={`sticky left-0 z-10 ${rowBg} px-4 py-3 align-top font-semibold text-stone-900 border-b border-stone-100 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]`}>
                          {c.title}
                        </td>
                        {c.levels.map((lvl, li) => (
                          <td
                            key={li}
                            className="px-4 py-3 align-top text-stone-700 leading-relaxed border-b border-stone-100 min-w-[200px]"
                          >
                            {lvl}
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Signup CTA */}
            <div className="mt-8 rounded-3xl bg-gradient-to-br from-brand-700 to-brand-800 text-white p-8 md:p-12 text-center shadow-xl shadow-brand-600/20">
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                Save your rubrics. Sign up free.
              </h3>
              <p className="text-brand-100 mb-6 max-w-xl mx-auto">
                Free accounts get 3 saved rubrics, 3 Learning Skills comments, and full history. No credit card.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-brand-800 font-bold rounded-xl text-base hover:bg-brand-50 transition-colors shadow-lg"
              >
                Sign up free
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}
      </main>

      <Footer />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function Nav() {
  return (
    <nav aria-label="Site" className="sticky top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-4 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <Logo className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
          <span className="text-lg font-bold text-stone-900 truncate">
            TeacherDashboard
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <Link
            to="/login"
            className="hidden sm:block text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            Sign up free
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 pt-12 pb-4">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/70 backdrop-blur-sm border border-stone-200 rounded-full text-stone-700 text-xs font-semibold uppercase tracking-[0.12em] mb-5 shadow-sm">
          <Sparkles className="w-3 h-3 text-brand-700" aria-hidden="true" />
          Free tool. No signup.
        </div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-stone-900 leading-[1.15] mb-4">
          Build a custom rubric in under a minute.
        </h1>
        <p className="text-base md:text-lg text-stone-600 max-w-xl mx-auto leading-relaxed">
          Tell us the assignment, we'll write the rubric. Edit it however you want, copy it into your gradebook, done.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 sm:px-6 lg:px-10 py-8 bg-white border-t border-stone-100">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="w-7 h-7" aria-hidden="true" />
          <span className="text-sm font-bold text-stone-900">TeacherDashboard</span>
        </Link>
        <div className="text-xs text-stone-500 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <Link to="/" className="hover:text-stone-900">Home</Link>
          <span className="text-stone-300">·</span>
          <Link to="/login" className="hover:text-stone-900">Sign in</Link>
          <span className="text-stone-300">·</span>
          <Link to="/privacy" className="hover:text-stone-900">Privacy</Link>
          <span className="text-stone-300">·</span>
          <Link to="/terms" className="hover:text-stone-900">Terms</Link>
          <span className="text-stone-300">·</span>
          <Link to="/contact" className="hover:text-stone-900">Contact</Link>
        </div>
        <div className="text-xs text-stone-500">© 2026 TeacherDashboard</div>
      </div>
    </footer>
  );
}
